/**
 * Composable for handling profile completion checks and redirects
 * Provides a consistent way to check if a user needs to complete their profile
 * and handle the redirect logic across authentication components
 */
export const useProfileCompletion = () => {
  const user = useUser()
  const route = useRoute()

  /**
   * Checks if the user needs to complete their profile and handles the redirect
   * @param {Object} options - Configuration options
   * @param {boolean} options.redirectBack - Whether to include intended URL in redirect (default: true)
   * @param {string} options.intendedRoute - Custom intended route (default: current route)
   * @param {Function} options.onRedirect - Callback to execute before redirect
   * @param {Function} options.onSkip - Callback to execute when profile completion is skipped
   * @returns {Promise<boolean>} - Returns true if redirect was performed, false otherwise
   */
  const checkAndRedirectToProfileCompletion = async ({
    redirectBack = true,
    intendedRoute = null,
    onRedirect = null,
    onSkip = null
  } = {}) => {
    // Skip if user is guest
    if (user.isGuest) {
      return false
    }

    // Skip if profile is already complete
    if (user.isProfileComplete) {
      return false
    }

    // Check if user has skipped profile completion before
    const hasSkippedThisSession = process.client ? sessionStorage.getItem('skipped-profile-completion-session') : null
    const hasSkippedPermanently = process.client ? localStorage.getItem('skipped-profile-completion') : null

    if (hasSkippedThisSession || hasSkippedPermanently) {
      // Execute skip callback if provided
      if (onSkip) {
        onSkip()
      }
      return false
    }

    // Get target path for intended destination
    const targetPath = intendedRoute ?? route.query.intended ?? route.fullPath ?? '/'
    const redirectUrl = `/onboarding/profile${redirectBack ? `?intended=${encodeURIComponent(targetPath)}` : ''}`

    // Execute redirect callback if provided
    if (onRedirect) {
      onRedirect(redirectUrl)
    }

    // Perform the redirect
    await navigateTo(redirectUrl)
    return true
  }

  /**
   * Clears profile completion skip flags
   * Used when user completes their profile
   */
  const clearSkipFlags = () => {
    if (process.client) {
      localStorage.removeItem('skipped-profile-completion')
      sessionStorage.removeItem('skipped-profile-completion-session')
    }
  }

  /**
   * Sets the session skip flag
   * Used when user chooses to skip profile completion for this session
   */
  const setSessionSkip = () => {
    user.setProfileCompletionSessionSkip()
  }

  /**
   * Checks if profile completion should be skipped
   * @returns {boolean} - True if profile completion should be skipped
   */
  const shouldSkipProfileCompletion = () => {
    if (user.isGuest || user.isProfileComplete) {
      return true
    }

    const hasSkippedThisSession = process.client ? sessionStorage.getItem('skipped-profile-completion-session') : null
    const hasSkippedPermanently = process.client ? localStorage.getItem('skipped-profile-completion') : null

    return !!(hasSkippedThisSession || hasSkippedPermanently)
  }

  return {
    checkAndRedirectToProfileCompletion,
    clearSkipFlags,
    setSessionSkip,
    shouldSkipProfileCompletion
  }
}
